import type { ReactElement, ReactNode } from 'react'
import { Check, AlertTriangle } from 'lucide-react'
import { AppShell } from '../components/AppShell.js'
import { Button } from '../components/ui/button.js'
import { Card } from '../components/ui/card.js'
import { Badge } from '../components/ui/badge.js'
import { useStep } from '../state/useStep.js'
import { useDemoStore } from '../state/store.js'
import { lookupModel } from '../lib/modelLookup.js'
import { activePersona } from '../lib/persona.js'

const Section = ({ title, children }: { title: string; children: ReactNode }): ReactElement => (
  <section className="mb-5">
    <div className="text-xs font-semibold tracking-wider text-primary uppercase mb-2">{title}</div>
    <Card className="p-5 divide-y divide-border">{children}</Card>
  </section>
)

const Row = ({ label, value, status = 'ok', onClick }: {
  label: string; value: string; status?: 'ok' | 'warn'; onClick?: () => void
}): ReactElement => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left flex items-center py-3 px-1 disabled:cursor-default"
    disabled={!onClick}
  >
    <div className="flex-1 min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
    <span
      className={
        status === 'ok'
          ? 'w-6 h-6 rounded-full border-2 border-[color:var(--color-ok-500)] text-[color:var(--color-ok-500)] flex items-center justify-center'
          : 'w-6 h-6 rounded-full border-2 border-[color:var(--color-warn-500)] text-[color:var(--color-warn-500)] flex items-center justify-center'
      }
    >
      {status === 'ok' ? <Check className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
    </span>
  </button>
)

export const ReviewScreen = (): ReactElement => {
  const { goto } = useStep()
  const { state } = useDemoStore()
  const persona = activePersona()
  const specs = state.modelLabel ? lookupModel(state.modelLabel.brand, state.modelLabel.model) : null
  const top = state.countertop
  const us = state.undersink
  const qa = state.qa
  const overhangWarn = (top?.overhangInches ?? 0) >= 1

  return (
    <AppShell
      title="Final review"
      showBack onBack={() => goto('qa')}
      progress={0.85}
      footer={<Button size="xl" onClick={() => goto('schedule')}>Pick your installation time</Button>}
    >
      <div className="text-center mb-4">
        <Badge variant="success">
          <Check className="h-3 w-3" /> All verified
        </Badge>
        <p className="text-sm text-muted-foreground mt-2">Here's everything we collected. Tap any row to edit.</p>
      </div>

      {top && (
        <Section title="Countertop">
          <Row label="Material" value={top.material} onClick={() => goto('countertop-results')} />
          <Row label="Overhang" value={`${top.overhangInches} inches`} status={overhangWarn ? 'warn' : 'ok'} onClick={() => goto('countertop-results')} />
        </Section>
      )}

      {us && (
        <Section title="Plumbing">
          <Row label="Shutoff valve" value={us.valve === 'quarter-turn' ? 'Standard quarter-turn shutoff' : us.valve} onClick={() => goto('undersink-results')} />
          <Row label="Drain connection" value={us.drain === 'standard-hose' ? 'Standard drain hose hookup' : us.drain} onClick={() => goto('undersink-results')} />
          <Row label="Electrical (under sink)" value={us.outlet.present ? `110V outlet detected (${us.outlet.side})` : 'No outlet visible'} onClick={() => goto('undersink-results')} />
          <Row label="Drain loop height (under sink)" value={`~${us.drainLoopInches} inches — ${us.drainLoopInches >= 32 ? 'meets code' : 'check measurement'}`} status={us.drainLoopInches >= 32 ? 'ok' : 'warn'} onClick={() => goto('undersink-results')} />
        </Section>
      )}

      {specs && (
        <Section title="Existing unit">
          <Row label="Brand / model" value={specs.displayName} onClick={() => goto('scan-model-label')} />
          <Row label="Electrical (dishwasher)" value={specs.electrical} />
        </Section>
      )}

      <Section title="Property">
        <Row label="Property type" value={persona.property.type} />
        <Row label="Floor / level" value={persona.property.floor} />
        <Row label="Year built" value={String(persona.property.yearBuilt)} />
        <Row label="Flooring" value={persona.prefill.flooring.value} />
      </Section>

      {qa && (
        <Section title="Access & issues">
          <Row label="Level / shimmed" value={qa.level === 'level' ? "It's level" : qa.level} onClick={() => goto('qa')} />
          <Row label="Water pressure" value={qa.waterPressure === 'normal' ? 'No, seems normal' : qa.waterPressure} onClick={() => goto('qa')} />
          <Row label="Known issues" value={qa.knownIssues} onClick={() => goto('qa')} />
        </Section>
      )}
    </AppShell>
  )
}
